import React from 'react';
import { useTranslation } from 'react-i18next';

import { useLocation, useSearchParams } from 'react-router-dom';

import { Button, Icon, Text, type IconName } from '../../../../components';
import { SparkleGlyph } from '../../../../layouts/MainLayout/components/SparkleGlyph';
import { ACQ, ACQ_TIMER_TONES, type TimerTone } from '../../../../theme/tokens';
import type { Campaign, CampaignTab, CampaignFilter, CampaignStatus } from '../../../../mock-data/types';
import {
  resolveCampaignStatus,
  formatStatusAt,
  useCampaignOverrides,
} from '../../../../utils/campaign-status';
import { CAMPAIGN_FILTER_LABELS } from './labels';
import { focusRecovery } from './browserFocus';
import {
  H2,
  TabIco,
  TabCount,
  TabDot,
  Grid,
  Cell,
  Empty,
  EmptyIcon,
  EmptyTitle,
  EmptyText,
  Card,
  Info,
  HeadRow,
  Period,
  Live,
  LiveDot,
  Main,
  Thumb,
  Title,
  TitleBox,
  JustActivated,
  Chev,
  Divider,
  Cta,
  ExpLabel,
  Timer,
  TUnit,
  TimerNum,
  TimerUnit,
  TSep,
  Meta,
  MetaIco,
  MetaVal,
  LoadMoreButton,
} from './Campaigns.styles';
// The browser chrome, shared with the operations Promotions browser: both group the
// same way (tab row on the page, one header row in the card, search on its own row).
import {
  Browser,
  TabsBar,
  BrowserTab,
  BrowserTabScroll,
  BrowserCard,
  BrowserHead,
  FilterLbl,
  CategoryChips,
  CategoryPill,
  LiveTabDot,
  SearchSlot,
  SearchControl,
  SearchIconBtn,
  SearchDivider,
  SearchDismiss,
  SearchRow,
  SearchPill,
  SearchField,
  SearchClear,
  SearchResults,
} from './CampaignBrowser.styles';

type TabId = CampaignTab['id'];

/** Ties the search toggle to the row it shows, via `aria-controls`. */
const SEARCH_ROW_ID = 'acquisition-campaigns-search';

/** Campaign cards shown per page; "Load more" reveals one more page of the current tab. */
const PAGE_SIZE = 4;

/** Per-tab empty-state icon (the copy is built reactively in-component via `emptyCopy`). */
const EMPTY_ICON_BY_TAB: Record<TabId, IconName> = {
  // The Suggestions tab uses the branded Lumio glyph (SparkleGlyph) rather than
  // this monochrome icon; kept as the conceptual fallback for the same spark.
  suggestions: 'sparkle',
  running: 'trending-up',
  paused: 'pause',
  ended: 'stop',
};

/**
 * The view is seeded ONCE from the URL query string (`?tab=&filter=&q=`), so a shared
 * link still opens to the right tab, category and search. It is not driven by the URL
 * after that: browsing does NOT navigate and does not update the URL, and Back does not
 * step through tab changes. See the note on the state declarations for why (a
 * same-document navigation snapped the scroll on iOS Safari). `?tab=` is validated
 * against the ids below and `?filter=` against the page's own `filters`; anything else
 * falls back to the default view ({@link DEFAULT_TAB}, no filter).
 */
const TAB_IDS: readonly TabId[] = ['suggestions', 'running', 'paused', 'ended'];
const isTabId = (value: string | null): value is TabId => value != null && (TAB_IDS as readonly string[]).includes(value);

/**
 * The tab the browser opens on: what the owner is actually running today, not
 * what Lumio proposes. Suggestions stay one click away (the tab row, the hero
 * CTA, and the Running tab's own empty state all lead there).
 */
const DEFAULT_TAB: TabId = 'running';

const statusForTab = (id: TabId): CampaignStatus => (id === 'suggestions' ? 'suggestion' : id);

const DAY = 86400;
const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);

/** Seconds left until `expiresAt` (epoch ms), clamped at 0, vs the browser clock. */
const secondsLeft = (expiresAt: number, now: number): number => Math.max(0, Math.round((expiresAt - now) / 1000));

type TimerSeg = { num: string; unit: string };
/**
 * Break the remaining seconds into display segments. Under a day it's the
 * familiar `HHh : MMm : SSs`; a day or more out it leads with `Dd`
 * (`Dd : HHh : MMm : SSs`). Seconds are always present so every card visibly
 * ticks down each second, not just once a minute.
 */
const timerSegments = (total: number): TimerSeg[] => {
  const d = Math.floor(total / DAY);
  const h = Math.floor((total % DAY) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const hms: TimerSeg[] = [{ num: pad(h), unit: 'h' }, { num: pad(m), unit: 'm' }, { num: pad(s), unit: 's' }];
  return d > 0 ? [{ num: String(d), unit: 'd' }, ...hms] : hms;
};

/** Urgency tone by time remaining: red < 2 days, gold < 5 days, grey beyond. */
const timerTone = (total: number): TimerTone => (total < 2 * DAY ? 'urgent' : total < 5 * DAY ? 'soon' : 'normal');

/** Ticks every second so the "Expires in" countdowns re-render live. */
const useNow = (): number => {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return now;
};

type TabTone = { dot: string | null; icon: 'pause' | 'stop' | null; badgeBg: string; badgeColor: string };
const tabTone = (tab: CampaignTab): TabTone => {
  if (tab.badgeColor === 'magenta') {
    // No dot: magenta is only ever the Suggestions tab, and that tab renders the Lumio
    // glyph instead, so a colour here would be unreachable. The badge still tints.
    return { dot: null, icon: null, badgeBg: ACQ.badgeSuggBg, badgeColor: ACQ.white };
  }
  if (tab.badgeColor === 'green') {
    return { dot: ACQ.badgeRunDot, icon: null, badgeBg: ACQ.badgeRunBg, badgeColor: ACQ.live };
  }
  return { dot: null, icon: tab.id === 'paused' ? 'pause' : 'stop', badgeBg: ACQ.badgeMutedBg, badgeColor: ACQ.t400 };
};

export type CampaignsProps = {
  tabs: CampaignTab[];
  filters: CampaignFilter[];
  pool: Campaign[];
  /** Open a campaign — id read from `event.currentTarget.dataset.id`. */
  onOpenCampaign: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
};

/** Imperative API for jumping the page straight into the campaigns browser. */
export type CampaignsHandle = {
  /** Switch to the Suggested tab and scroll the browser into view. */
  revealSuggestions: () => void;
};

/**
 * Campaigns browser. The tab row sits on the page, above a card that opens on a single
 * header row: the "Category" chips (an "All" chip plus one per category) on the left and
 * the search hard right. Below it, the two-column grid of campaign cards (period/thumb/
 * title, then the "Expires in" countdown for a suggestion or the paused/ended date) and
 * "Load more" pagination.
 */
export const Campaigns = React.forwardRef<CampaignsHandle, CampaignsProps>(function Campaigns(
  { tabs, filters, pool, onOpenCampaign },
  ref,
): React.ReactElement {
  const { t } = useTranslation();
  const now = useNow();
  const overrides = useCampaignOverrides();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const searchRef = React.useRef<HTMLInputElement>(null);
  // Focus lands back here whenever the search row unmounts: the element that had focus
  // goes with it, and without this the browser drops focus to <body>, so the next Tab
  // restarts at the top of the page instead of where the owner was (WCAG 2.4.3).
  const searchToggleRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  // Set by clearSearch, consumed by the layout effect that actually moves focus.
  const focusAfterClear = React.useRef(false);
  // Set by the inline clear and the category chips: unlike clearSearch they only unmount
  // themselves when they empty the view, so the effect gates this one on that transition.
  const focusAfterNarrow = React.useRef(false);

  // The campaign just activated from its suggested-detail page (passed via
  // navigation state on the redirect to the Running tab). Read once at mount: its
  // card gets the green hue (self-fading) and the "Just activated" tagline (which
  // lives until the owner navigates away — i.e. this component unmounts).
  const [justActivatedId] = React.useState<string | null>(
    () => (location.state as { activatedId?: string } | null)?.activatedId ?? null,
  );

  // Arriving from an activation, bring the table into view so the owner lands on
  // the just-activated card (on the Running tab) rather than the top of the page.
  // Deferred a frame so the page-transition mount has laid out. scrollIntoView uses
  // layout position (not the transition's transform), so it targets correctly.
  React.useEffect(() => {
    if (justActivatedId == null) return;
    const raf = requestAnimationFrame(() => {
      panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(raf);
  }, [justActivatedId]);

  // The campaigns view (tab / category / search) is LOCAL state, seeded once from the
  // URL so a shared `?tab=…&filter=…&q=…` link still opens to the right view — but
  // switching it does NOT navigate. On mobile the document is the scroller, and a
  // same-document navigation makes iOS Safari re-reveal its toolbar and snap the
  // scroll; driving tabs from the URL therefore jumped the page on every click.
  // Local state avoids the navigation entirely. (Trade-off: the URL no longer
  // updates as you browse, and Back no longer steps back through tabs.)
  const [tab, setTab] = React.useState<TabId>(() => {
    const raw = searchParams.get('tab');
    return isTabId(raw) ? raw : DEFAULT_TAB;
  });
  const [filter, setFilter] = React.useState<string | null>(() => {
    const raw = searchParams.get('filter');
    return raw && filters.some((f) => f.value === raw) ? raw : null;
  });
  const [query, setQueryState] = React.useState<string>(() => searchParams.get('q') ?? '');

  // Presentational-only view.
  const [searchOpen, setSearchOpen] = React.useState<boolean>(() => Boolean((searchParams.get('q') ?? '').trim()));
  const [pages, setPages] = React.useState(1);

  // Exposed so a parent CTA (the hero's "Activate a new campaign") can drop the
  // user straight onto the Suggested tab with the browser scrolled into view.
  // The scroll target clears the overlay header via the Browser's scroll-margin-top.
  React.useImperativeHandle(
    ref,
    () => ({
      revealSuggestions: () => {
        setTab('suggestions');
        setPages(1);
        panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      },
    }),
    [],
  );

  // Reconcile each campaign's stored status with any runtime pause/resume override
  // and the expiry rule (a once-run campaign past its deadline reads as ended), so
  // the tabs, counts and cards all reflect the live state. `now` ticks each second,
  // so a campaign flips tab the instant it expires. A campaign ended by expiry
  // shows that deadline as its "Ended on" date; a freshly-paused one shows when it
  // was paused.
  const resolvedPool = React.useMemo<Campaign[]>(
    () =>
      pool.map((c) => {
        const override = overrides[c.id];
        const resolved = resolveCampaignStatus(c.status, c.expiresAt, override?.status, now);
        let statusAt = c.statusAt;
        if (resolved === 'ended' && c.status !== 'ended' && c.expiresAt != null) statusAt = formatStatusAt(c.expiresAt);
        else if (resolved === 'paused' && override?.status === 'paused') statusAt = formatStatusAt(override.at);
        return resolved === c.status && statusAt === c.statusAt ? c : { ...c, status: resolved, statusAt };
      }),
    [pool, overrides, now],
  );

  // Each tab's badge shows how many campaigns actually carry its (resolved) status,
  // tallied straight from the pool — so the number can never drift from the rows.
  const counts = React.useMemo(() => {
    const tally: Record<CampaignStatus, number> = { suggestion: 0, running: 0, paused: 0, ended: 0 };
    for (const c of resolvedPool) tally[c.status] += 1;
    return tally;
  }, [resolvedPool]);

  // Anything that changes which rows are listed resets pagination to the first page.
  // Showing or hiding the search field does not: that changes what is on screen, not
  // which campaigns match.
  const selectTab = (id: TabId): void => {
    setTab(id);
    setPages(1);
  };
  const selectFilter = (value: string | null): void => {
    setFilter(value);
    setPages(1);
    // Clearing to "All" can empty the view and unmount this chip; the effect recovers
    // focus, but only if that actually happens (it checks the empty-view transition).
    focusAfterNarrow.current = true;
  };
  const setQuery = (value: string): void => {
    setQueryState(value);
    setPages(1);
  };
  const loadMore = (): void => setPages((p) => p + 1);
  // Purely show/hide: hiding the field deliberately KEEPS the term, so collapsing
  // the row never silently changes which campaigns are listed. What tells the owner
  // a hidden term is still filtering is the toggle's own active colour; the inline
  // clear inside the row is what actually drops the term.
  const toggleSearch = (): void => {
    // Hiding unmounts the row, and with it whatever inside it had focus, so pull
    // focus back to this toggle first.
    if (searchOpen) searchToggleRef.current?.focus();
    setSearchOpen(!searchOpen);
  };
  // Leaves search altogether: drops the term and closes the field. Reached two ways,
  // the chip's dismiss and Escape inside the field, because both read as "I am done
  // searching" rather than "put this away for a moment". Distinct from the clear
  // inside the field, which only empties the text and keeps it open for the next
  // attempt, and from the toggle, which hides the field but deliberately keeps the
  // term applied.
  const clearSearch = (): void => {
    setQueryState('');
    setSearchOpen(false);
    setPages(1);
    // Both callers go away with the row they live in (the dismiss renders only while
    // a term is hidden; the field unmounts on close), so hand focus to the toggle
    // rather than letting it fall to <body>.
    //
    // Handed to the post-commit effect below rather than done here: dropping the term
    // can ALSO empty the view, and on a tab that only had matches because of the term
    // that makes isEmptyView true and unmounts the whole header row, toggle included.
    // Focusing synchronously targeted an element that was about to be removed, which
    // put focus on <body> (WCAG 2.4.3).
    focusAfterClear.current = true;
  };

  React.useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  const term = query.trim();
  const q = term.toLowerCase();
  const status = statusForTab(tab);
  const matches = resolvedPool.filter(
    (c) => c.status === status && (!filter || c.category === filter) && (!q || c.title.toLowerCase().includes(q)),
  );
  // Surface a just-activated campaign at the top of its (Running) list so the
  // green hue + "Just activated" tagline are visible regardless of pool order or
  // pagination; otherwise the natural pool order stands.
  const activatedIdx = justActivatedId ? matches.findIndex((c) => c.id === justActivatedId) : -1;
  const ordered =
    activatedIdx > 0
      ? [matches[activatedIdx], ...matches.slice(0, activatedIdx), ...matches.slice(activatedIdx + 1)]
      : matches;
  const shown = ordered.slice(0, pages * PAGE_SIZE);
  const hasMore = shown.length < ordered.length;

  // The open tab holds nothing AND nothing of the owner's is hiding it, so the empty
  // message IS the whole view: the search box and category chips are suppressed, since
  // there is nothing there to search or narrow. Deliberately scoped to this exact case
  // rather than to `matches.length === 0`: when a search or category emptied the list,
  // that same chrome is the only way back out of the state, and hiding it would strand
  // the owner on a view with no campaigns and no control to restore them. The condition
  // reads the OPEN tab, not the pool, so a populated tab keeps its header row while an
  // empty sibling drops it.
  const isEmptyView = matches.length === 0 && !term && !filter;

  // Keeps keyboard focus on a real control whenever a header control tears down the row
  // it lives in, which would otherwise drop focus to <body> and restart the next Tab at
  // the top of the page (WCAG 2.4.3). Only ever acts on a USER action, via a flag one of
  // the handlers set: it must NOT react to a passive re-render, because Campaigns ticks
  // every second (useNow) and a lone running campaign can expire out of the Running tab
  // while the owner is just watching, emptying the view with focus resting on <body> --
  // grabbing focus (and scrolling the tab into view) on a timer would be the bug, not the
  // fix. Two flags, because the two cases want different targets:
  //  - clearSearch (Escape / the chip dismiss) always unmounts the field it ran from. On
  //    a populated tab the toggle beside it survives, so focus goes there; if the term
  //    was the only match the whole row went too, so it falls back to the open tab.
  //  - The inline clear or the "All"/category chip only removes itself when it EMPTIES the
  //    view (`becameEmpty`); a narrowing that leaves the row up is excluded, so focus is
  //    left on the chip the owner clicked. When it does empty, focus goes to the open tab.
  // Focus targets the OPEN tab via aria-pressed (the tab row outside the card always
  // survives), with preventScroll so recovery never moves the viewport. `wasEmptyView`
  // seeds from the initial value, so an already-empty first paint is not a transition.
  const wasEmptyView = React.useRef(isEmptyView);
  React.useLayoutEffect(() => {
    const becameEmpty = isEmptyView && !wasEmptyView.current;
    wasEmptyView.current = isEmptyView;
    const recovery = focusRecovery({
      afterClear: focusAfterClear.current,
      afterNarrow: focusAfterNarrow.current,
      becameEmpty,
    });
    focusAfterClear.current = false;
    focusAfterNarrow.current = false;
    if (recovery === 'none') return;
    const activeTab = panelRef.current?.querySelector<HTMLButtonElement>('[aria-pressed="true"]');
    const toggle = searchToggleRef.current;
    // 'toggle-or-tab' prefers the surviving toggle; 'tab' and a gone toggle both land on
    // the open tab. preventScroll so recovery never moves the viewport.
    const target = recovery === 'toggle-or-tab' && toggle?.isConnected ? toggle : activeTab;
    target?.focus({ preventScroll: true });
  });

  // Tab/filter display labels (imported as stable English keys, shared with the
  // Operations browser) translated at this render site over the known vocabulary.
  const tabLabel = (id: TabId): string => {
    const labels: Record<TabId, string> = {
      suggestions: t('Suggestions'),
      running: t('Running'),
      paused: t('Paused'),
      ended: t('Ended'),
    };
    return labels[id];
  };
  const filterChipLabel = (value: string): string => {
    const labels: Record<string, string> = {
      seasonal: t('Seasonal'),
      sports: t('Sports'),
      promo: t('Promo'),
      acquisition: t('Acquisition'),
      retention: t('Retention'),
    };
    return labels[value] ?? CAMPAIGN_FILTER_LABELS[value] ?? value;
  };

  // Per-tab empty-state copy, built during render so it re-translates on a live
  // language switch (the icon is the static `EMPTY_ICON_BY_TAB`).
  const emptyCopy = (id: TabId): { title: string; body: string } => {
    const copy: Record<TabId, { title: string; body: string }> = {
      suggestions: {
        title: t('New campaigns are on the way'),
        body: t(
          'Lumio is studying your players and preparing fresh campaigns to bring more of them in. They will appear here ready to launch.',
        ),
      },
      running: {
        title: t('No campaigns are live'),
        body: t('Activate a suggested campaign to launch your first one, it shows up here while it runs.'),
      },
      paused: {
        title: t('Nothing paused'),
        body: t('Campaigns you pause will wait here until you resume them.'),
      },
      ended: {
        title: t('No ended campaigns'),
        body: t('Campaigns are archived here once they finish.'),
      },
    };
    return copy[id];
  };

  const filterLabel = filter ? filterChipLabel(filter) : '';
  const emptyMsg =
    filter && term
      ? t('No {{filterLabel}} campaigns match “{{term}}”.', { filterLabel, term })
      : term
        ? t('No campaigns match “{{term}}”.', { term })
        : filter
          ? t('No {{filterLabel}} campaigns in this view.', { filterLabel })
          : t('No campaigns in this view.');

  return (
    <>
      <H2 text="Campaigns" tag="span" />
      <Browser ref={panelRef}>
        <TabsBar>
          <BrowserTabScroll>
            {tabs.map((item) => {
              const tone = tabTone(item);
              const count = counts[statusForTab(item.id)];
              // The dot breathes only when something is actually live. A heartbeat over
              // "No campaigns are live" advertises liveness the same screen denies, so an
              // empty tab keeps the plain dot.
              const Dot = count > 0 ? LiveTabDot : TabDot;
              return (
                <BrowserTab
                  key={item.id}
                  $active={tab === item.id}
                  type="button"
                  // A toggle button, like the category chips in the same row: the fill,
                  // hairline and weight bump are all visual, so the open view is also
                  // announced here. (The empty-view focus fallback finds the open tab
                  // through this attribute.)
                  aria-pressed={tab === item.id}
                  onClick={() => selectTab(item.id)}
                >
                  {item.id === 'suggestions' ? (
                    // Suggestions are Lumio's, so the tab is marked by the product's
                    // own glyph rather than a status dot (Figma node 9489:2066). The
                    // same glyph heads this tab's empty state below.
                    <TabIco>
                      <SparkleGlyph size={16} />
                    </TabIco>
                  ) : tone.dot ? (
                    // Only Running still uses a dot (Suggestions took the Lumio glyph
                    // above), so this is the running-campaigns heartbeat and nothing else.
                    <Dot $color={tone.dot} />
                  ) : tone.icon ? (
                    <TabIco>
                      <Icon name={tone.icon} size={14} color={ACQ.t400} />
                    </TabIco>
                  ) : null}
                  <Text text={tabLabel(item.id)} tag="span" />
                  <TabCount $bg={tone.badgeBg} $color={tone.badgeColor}>
                    <Text text={String(count)} tag="span" />
                  </TabCount>
                </BrowserTab>
              );
            })}
          </BrowserTabScroll>
        </TabsBar>

        <BrowserCard>
          {isEmptyView ? null : (
            <BrowserHead>
              <FilterLbl text="Category" tag="span" />
              <CategoryChips>
                {/* "All" is the neutral state (no category applied) rather than a
                    category of its own, so it reads as pressed exactly when nothing
                    is filtering. It replaces the old "Clear all filters" link: with
                    the chips always on screen there is no separate control to undo
                    them. */}
                <CategoryPill
                  $on={filter === null}
                  type="button"
                  aria-pressed={filter === null}
                  onClick={() => selectFilter(null)}
                >
                  <Text text="All" tag="span" />
                </CategoryPill>
                {filters.map((f) => (
                  <CategoryPill
                    key={f.value}
                    $on={filter === f.value}
                    type="button"
                    aria-pressed={filter === f.value}
                    onClick={() => selectFilter(filter === f.value ? null : f.value)}
                  >
                    <Text text={filterChipLabel(f.value)} tag="span" />
                  </CategoryPill>
                ))}
              </CategoryChips>
              <SearchSlot>
                <SearchControl $on={Boolean(term)}>
                  <SearchIconBtn
                    ref={searchToggleRef}
                    type="button"
                    aria-label={searchOpen ? t('Hide search') : t('Search campaigns')}
                    aria-expanded={searchOpen}
                    aria-controls={SEARCH_ROW_ID}
                    onClick={toggleSearch}
                  >
                    <Icon name="search" size={16} />
                  </SearchIconBtn>
                  {!searchOpen && term ? (
                    // Offered exactly when the field is HIDDEN but a term is still
                    // filtering: that is the state with no other way out, since
                    // hiding deliberately keeps the term and the clear inside the
                    // field went off screen with it. While the field is open that
                    // inline clear is right there, so a second one here would be
                    // redundant.
                    <>
                      <SearchDivider aria-hidden="true" />
                      <SearchDismiss type="button" aria-label={t('Clear search')} onClick={clearSearch}>
                        <Icon name="close" size={16} />
                      </SearchDismiss>
                    </>
                  ) : null}
                </SearchControl>
              </SearchSlot>
            </BrowserHead>
          )}

          {!isEmptyView && searchOpen ? (
            <SearchRow id={SEARCH_ROW_ID}>
              <SearchPill>
                <Icon name="search" size={18} />
                <SearchField
                  ref={searchRef}
                  type="text"
                  aria-label={t('Search campaigns')}
                  placeholder={t('Search campaigns…')}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={(event) => {
                    // Escape LEAVES search (drops the term), rather than merely hiding
                    // the row the way the toggle does: dismissing a field you are
                    // typing in means you are done with the filter, so leaving it
                    // silently applied behind a collapsed row is the wrong default.
                    if (event.key === 'Escape') clearSearch();
                  }}
                />
                {query ? (
                  <SearchClear
                    type="button"
                    aria-label={t('Clear search text')}
                    onClick={() => {
                      setQuery('');
                      // Normally the field stays and keeps focus; if clearing empties the
                      // view the field unmounts, and the effect moves focus to the tab.
                      focusAfterNarrow.current = true;
                      searchRef.current?.focus();
                    }}
                  >
                    <Icon name="close" size={14} />
                  </SearchClear>
                ) : null}
              </SearchPill>
              {term ? (
                // Counted off `matches`, i.e. every campaign the term found, not the
                // `shown` page of them: the pager below reveals the rest. `count` is
                // i18next's plural trigger, so this reads "1 result" in the singular.
                <SearchResults text="{{count}} results" placeholders={{ count: matches.length }} tag="span" />
              ) : null}
            </SearchRow>
          ) : null}

          <Grid>
            {matches.length > 0 ? (
              <>
                {shown.map((campaign) => {
                  const left = campaign.expiresAt != null ? secondsLeft(campaign.expiresAt, now) : null;
                  const tone = left != null ? ACQ_TIMER_TONES[timerTone(left)] : null;
                  return (
                    <Cell key={campaign.id}>
                      <Card
                        data-id={campaign.id}
                        role="button"
                        tabIndex={0}
                        $justActivated={campaign.id === justActivatedId}
                        aria-label={t('Open {{name}}', { name: campaign.title })}
                        onClick={onOpenCampaign}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onOpenCampaign(event);
                          }
                        }}
                      >
                        <Info>
                          <HeadRow>
                            <Period text="Campaign period: {{period}}" placeholders={{ period: campaign.period }} tag="span" />
                            {campaign.status === 'running' ? (
                              <Live>
                                <LiveDot />
                                <Text text="LIVE" tag="span" />
                              </Live>
                            ) : null}
                          </HeadRow>
                          <Main>
                            <Thumb src={campaign.image} alt="" />
                            <TitleBox>
                              <Title text={campaign.title} tag="span" />
                              {campaign.id === justActivatedId ? (
                                <JustActivated text="Just activated" tag="span" />
                              ) : null}
                            </TitleBox>
                            <Chev aria-hidden>
                              <Icon name="arrow-right-circle" size={24} color={ACQ.t50} />
                            </Chev>
                          </Main>
                        </Info>

                        {campaign.status === 'suggestion' && left != null && tone ? (
                          <>
                            <Divider />
                            <Cta>
                              <ExpLabel text="Expires in" tag="span" />
                              <Timer>
                                {timerSegments(left).map((seg, i) => (
                                  <React.Fragment key={seg.unit}>
                                    {i > 0 ? <TSep text=":" tag="span" $color={tone.sep} /> : null}
                                    <TUnit $color={tone.num}>
                                      <TimerNum text={seg.num} tag="span" />
                                      <TimerUnit text={seg.unit} tag="span" />
                                    </TUnit>
                                  </React.Fragment>
                                ))}
                              </Timer>
                            </Cta>
                          </>
                        ) : null}

                        {(campaign.status === 'paused' || campaign.status === 'ended') && campaign.statusAt ? (
                          <>
                            <Divider />
                            <Meta>
                              <MetaIco>
                                <Icon name={campaign.status === 'paused' ? 'pause' : 'stop'} size={13} color={ACQ.t400} />
                              </MetaIco>
                              {campaign.status === 'paused' ? (
                                <ExpLabel text="Paused on" tag="span" />
                              ) : (
                                <ExpLabel text="Ended on" tag="span" />
                              )}
                              <MetaVal text={campaign.statusAt} tag="span" />
                            </Meta>
                          </>
                        ) : null}
                      </Card>
                    </Cell>
                  );
                })}
                {hasMore ? (
                  <LoadMoreButton variant="text" icon="plus-circle" label={t('Load more campaigns')} onClick={loadMore} />
                ) : null}
              </>
            ) : term || filter ? (
              // A search/category is on but nothing matched it — a "no results" state
              // distinct from a genuinely empty tab, and the reason the header row above
              // is NOT suppressed here. The ways back all live in that row: the "All"
              // chip, the clear inside the search field, and the chip's dismiss.
              <Empty>
                <EmptyIcon>
                  <Icon name="search" size={24} color={ACQ.t400} />
                </EmptyIcon>
                <EmptyTitle text="No matches" tag="span" />
                <EmptyText text={emptyMsg} tag="span" />
              </Empty>
            ) : (
              // The tab itself is empty — tailored copy for this state.
              <Empty>
                <EmptyIcon>
                  {tab === 'suggestions' ? (
                    <SparkleGlyph size={30} />
                  ) : (
                    <Icon name={EMPTY_ICON_BY_TAB[tab]} size={24} color={ACQ.t400} />
                  )}
                </EmptyIcon>
                <EmptyTitle text={emptyCopy(tab).title} tag="span" />
                <EmptyText text={emptyCopy(tab).body} tag="span" />
                {/* Offered only when there is actually something to browse: with no
                    suggestions waiting, this button sent the owner from one empty tab
                    to another, which reads as a broken control rather than a way
                    forward. The count is the same tally the Suggestions badge shows,
                    so the two can never disagree. */}
                {tab === 'running' && counts.suggestion > 0 ? (
                  <Button variant="outline" icon="sparkle" label={t('Browse suggestions')} onClick={() => selectTab('suggestions')} />
                ) : null}
              </Empty>
            )}
          </Grid>
        </BrowserCard>
      </Browser>
    </>
  );
});

export default Campaigns;
